// ABOUTME: Reach tiles for screens 3A and 4A: toggle buttons on 3A, and on 4A a read-only list with the picked tiles lit.
// ABOUTME: A 2x2 block centred over the stack, sized in the stack's design pixel; a selected tile is never marked by colour alone.

import { Box, Button, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import {
  CROSSING_HAIRLINE, CROSSING_PILL_INK, CROSSING_TEAL_EYEBROW,
  CROSSING_FOCUS_OUTLINE, CROSSING_IMPACT_TILE_BG, CROSSING_IMPACT_TILE_SELECTED_BG,
  CROSSING_IMPACT_TILE_SELECTED_BORDER, CROSSING_IMPACT_TILE_SELECTED_INK, CROSSING_IMPACT_TILE_CLUE_INK, CROSSING_IMPACT_TILE_DIM,
} from '../../../../theme/crossingTokens';
import type { CrossingReachTypeDto } from '../../../../types/api';
import { visuallyHidden } from '../../runner/visuallyHidden';

interface ReachTilesProps {
  reachTypes: CrossingReachTypeDto[];
  selected: string[];
  onToggle?: (code: string) => void;
  // Draws the picks as a list instead of controls: nothing focusable, no landing animation (4A shows them already in place).
  readOnly?: boolean;
}

const READ_ONLY_LIST_LABEL = 'What it reached';
const REACHED_MARKER = 'Reached';

// One unit is a v28 design pixel (--impact-px, set on the entrance): tiles 400 x 128 with an 18 / 16 gap, two across.
const UNIT = 'var(--impact-px)';
const units = (amount: number) => `calc(${UNIT} * ${amount})`;
const TILE_LAND_SEC = 3.3;
const TILE_LAND_STAGGER_SEC = 0.18;
const TILE_LAND_DURATION_SEC = 0.9;

const TILE_UNSELECTED = { bg: CROSSING_IMPACT_TILE_BG, border: CROSSING_HAIRLINE, ink: CROSSING_PILL_INK };
const TILE_SELECTED = { bg: CROSSING_IMPACT_TILE_SELECTED_BG, border: CROSSING_IMPACT_TILE_SELECTED_BORDER, ink: CROSSING_IMPACT_TILE_SELECTED_INK };

const land = keyframes`
  from { opacity: 0; transform: scale(0.94); }
  to { opacity: 1; transform: scale(1); }
`;

export default function ReachTiles({ reachTypes, selected, onToggle, readOnly = false }: ReachTilesProps) {
  const reducedMotion = useCrossingReducedMotion();
  const tileTransition = reducedMotion ? 'none' : 'background-color 0.4s ease, border-color 0.4s ease, opacity 0.4s ease';

  return (
    <Box
      data-testid="reach-tiles"
      {...(readOnly ? { component: 'ul', 'aria-label': READ_ONLY_LIST_LABEL } : {})}
      sx={{
        ...(readOnly ? { listStyle: 'none', m: 0, p: 0 } : {}),
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 5,
        width: units(818),
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: units(18), rowGap: units(16),
        // The block never catches a pointer in its gaps; each tile turns it back on.
        pointerEvents: 'none',
      }}
      // Never wider than the frame less a 16px gutter each side (the entrance pads 24px a side).
      style={{ maxWidth: 'calc(100cqw + 16px)' }}
    >
      {reachTypes.map((reach, index) => {
        const isSelected = selected.includes(reach.code);
        const dim = !isSelected && selected.length > 0;
        // The landing fill is 'backwards' so the animation's end state does not override the dim after it plays.
        const palette = isSelected ? TILE_SELECTED : TILE_UNSELECTED;
        const tileSx = {
          animationName: reducedMotion || readOnly ? 'none' : `${land}`, animationTimingFunction: 'ease', animationFillMode: 'backwards',
          transition: tileTransition, position: 'relative', opacity: dim ? CROSSING_IMPACT_TILE_DIM : 1,
          width: '100%', aspectRatio: '400 / 128', borderRadius: '18px', boxSizing: 'border-box',
          backgroundColor: palette.bg, border: `1.5px solid ${palette.border}`, color: palette.ink,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: units(4),
          padding: `${units(10)} ${units(12)}`, textTransform: 'none', lineHeight: 1,
        } as const;
        const content = (
          <>
            {/* The glyphs are decoration: aria-pressed already says selected, and the icon adds noise to the name. */}
            <Typography aria-hidden sx={{ fontSize: `max(13px, ${units(30)})`, lineHeight: 1 }}>{reach.icon}</Typography>
            <Typography sx={{ fontFamily: 'Newsreader, serif', fontSize: `max(12px, ${units(31)})`, fontWeight: 600, lineHeight: 1.2 }}>
              {reach.name}
            </Typography>
            <Typography sx={{ fontSize: `max(9px, ${units(19.4)})`, lineHeight: 1.25, color: CROSSING_IMPACT_TILE_CLUE_INK }}>{reach.clues}</Typography>
            {isSelected && (
              <Typography aria-hidden sx={{ position: 'absolute', top: units(6), right: units(9), fontSize: `max(12px, ${units(19)})`, lineHeight: 1, color: CROSSING_TEAL_EYEBROW }}>
                ✓
              </Typography>
            )}
          </>
        );

        // A read-only tile says it was reached in text as well as by its colour and check.
        if (readOnly) {
          return (
            <Box key={reach.code} component="li" sx={tileSx}>
              {content}
              {isSelected && <Box component="span" sx={visuallyHidden}>{REACHED_MARKER}</Box>}
            </Box>
          );
        }

        return (
          <Button
            key={reach.code}
            aria-pressed={isSelected}
            onClick={() => onToggle?.(reach.code)}
            style={reducedMotion ? undefined : {
              animationDuration: `${TILE_LAND_DURATION_SEC}s`,
              animationDelay: `${Number((TILE_LAND_SEC + index * TILE_LAND_STAGGER_SEC).toFixed(2))}s`,
            }}
            sx={{
              ...tileSx, pointerEvents: 'auto',
              '&:hover': { backgroundColor: palette.bg },
              '&:focus-visible': { outline: `2px solid ${CROSSING_FOCUS_OUTLINE}`, outlineOffset: '2px' },
            }}
          >
            {content}
          </Button>
        );
      })}
    </Box>
  );
}
